import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { get, omit, merge, keys, set, filter } from "lodash-es";

import { IDataKeys, IInfoBlock } from "shared/types";
import { IInfoBlockId, IVenue, IVenueActions } from "Venues/types";
import { IJSONAPIData, IActionStates, IDataState } from "shared/types";

import {
  createInfoBlock,
  deleteInfoBlock,
  deleteStageCapacity,
  editStageCapacity,
  revokeEditorInvite,
} from "./actions";
import { getInfoBlockItem } from "Venues/utils";
import {
  getMergedVenueState,
  isPendingAction,
  isCreatedFulfilledAction,
  isDeletedFulfilledAction,
  isFulfilledAction,
  isRejectedAction,
  isReplaceFulfilledAction,
  isVenueDetailFulfilledAction,
  isEditFulfilledAction,
  isPaginatedFulfilledAction,
  isInfoBlockDetailFulfilledAction,
} from "./utils";
import {
  setStateFulfilled,
  setStateLoading,
  setStateRejected,
  setStatePagination,
  getAction,
  infoBlockDetailMatcherHelper,
} from "shared/state";
import { loggerService } from "shared/services";
import { API_ENDPOINTS } from "shared/constants";

const initialState: IDataState = {
  data: {},
};

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    clearActionMeta: (state, { payload }: PayloadAction<IVenueActions>) => {
      state.meta = omit(state.meta, payload);
    },
    clearActionPagination: (state, { payload }: PayloadAction<IVenueActions>) => {
      state.pagination = omit(state.pagination, payload);
    },
    clearVenueState: () => {
      loggerService.log({
        icon: "🆑",
        title: "Venue State",
        body: "Clearing venue state",
      });
      return initialState;
    },
  },
  extraReducers: builder => {
    // edit stage capacity: update stage capacity in stage in redux
    builder.addCase(editStageCapacity.fulfilled, (state, { payload, meta }) => {
      if (!payload) return;

      const capacities = payload[IDataKeys.stageCapacity];
      const capacity = capacities[keys(capacities)[0]];
      const stageId = API_ENDPOINTS.STAGE(meta.arg.stageId);

      if (!state.data.stage) return;

      let stageCapacities = get(
        state.data.stage[stageId].attributes,
        "stageCapacities"
      ) as IJSONAPIData[];

      if (!stageCapacities) return;

      // replace the edited stage in stageCapacities
      stageCapacities = stageCapacities.map((stageCapacity: IJSONAPIData) =>
        stageCapacity.data.id === capacity.id ? { data: capacity } : stageCapacity
      );

      state.data.stage[stageId] = merge({}, state.data.stage?.[stageId], {
        attributes: { stageCapacities },
      });
    });

    // delete stage capacity: also delete stage capacity from stage in redux
    builder.addCase(deleteStageCapacity.fulfilled, (state, { meta }) => {
      const deletedId = API_ENDPOINTS.STAGE_CAPACITY(meta.arg.id);
      const stageId = API_ENDPOINTS.STAGE(meta.arg.stageId);

      if (!state.data.stage) return;

      let stageCapacities = get(
        state.data.stage[stageId].attributes,
        "stageCapacities"
      ) as IJSONAPIData[];

      if (!stageCapacities) return;

      // replace the edited stage in stageCapacities
      stageCapacities = filter(
        stageCapacities,
        stageCapacity => stageCapacity.data.id !== deletedId
      );

      state.data.stage[stageId] = merge(
        {},
        omit(state.data.stage?.[stageId], "attributes.stageCapacities"),
        {
          attributes: { stageCapacities },
        }
      );
    });

    // create info block: add new info block id to stage or venue
    // info block is added to state by isCreatedFulfilledAction matcher
    builder.addCase(createInfoBlock.fulfilled, (state, { payload, meta }) => {
      if (payload) {
        const infoBlocks = payload[keys(payload)[0]];
        const block: unknown = infoBlocks[keys(infoBlocks)[0]];

        const { id, attributes, type } = block as {
          id: string;
          attributes: IInfoBlock;
          type: string;
        };

        const infoBlockType = attributes.type ? getInfoBlockItem(attributes.type) : "";

        const newItem = {
          data: {
            id,
            type,
          },
        };

        if (infoBlockType) {
          const { type } = infoBlockType;

          if ("stage" in meta.arg && state.data.stage) {
            const stageId = meta.arg.stage;
            if (stageId) {
              const infoBlocks: IInfoBlockId[] =
                (state.data.stage[stageId].attributes?.infoBlocks as any) || [];
              const newInfoBlocks = infoBlocks.map(value =>
                value.infoBlockType === type ? { ...value, infoBlock: newItem } : value
              );

              state.data.stage[stageId] = merge({}, state.data.stage?.[stageId], {
                attributes: {
                  infoBlocks: newInfoBlocks,
                },
              });
            }
          } else if ("venue" in meta.arg && state.data.venue) {
            const venueId = meta.arg.venue;
            if (venueId) {
              const infoBlocks: IInfoBlockId[] =
                (state.data.venue[venueId].attributes?.infoBlocks as any) || [];
              const newInfoBlocks = infoBlocks.map(value =>
                value.infoBlockType === type ? { ...value, infoBlock: newItem } : value
              );

              state.data.venue[venueId] = merge({}, state.data.venue?.[venueId], {
                attributes: {
                  infoBlocks: newInfoBlocks,
                },
              });
            }
          }
        }
      }
    });

    // delete info block: remove info block id from stage or venue
    // info block is removed from the state by isDeletedFulfilledAction matcher
    builder.addCase(deleteInfoBlock.fulfilled, (state, { payload }) => {
      const { type } = payload;
      const infoBlockType = getInfoBlockItem(type);

      if (infoBlockType) {
        const { type } = infoBlockType;

        // remove info block id from stage or venue state
        if ("stage" in payload && !!payload["stage"]) {
          const stageId =
            typeof payload.stage === "string" ? payload.stage : (payload.stage as any).id;
          if (state.data.stage?.[stageId]) {
            const newVenueData = set(state.data.stage[stageId].attributes as any, type, null);
            state.data.stage[stageId].attributes = newVenueData;
          }
        } else if ("venue" in payload && !!payload["venue"]) {
          const venueId =
            typeof payload.venue === "string" ? payload.venue : (payload.venue as any).id;
          if (state.data.venue?.[venueId]) {
            const newVenueData = set(state.data.venue[venueId].attributes as any, type, null);
            state.data.venue[venueId].attributes = newVenueData;
          }
        }
      }
    });

    // Revoke editor invite, remove from venue
    builder.addCase(revokeEditorInvite.fulfilled, (state, { meta }) => {
      const { venueId, inviteId } = meta.arg;
      const venuePath = API_ENDPOINTS.VENUE(venueId);

      if (state.data.venue?.[venuePath]?.attributes !== undefined) {
        const venueAttributes = state.data.venue[venuePath].attributes as unknown as IVenue;
        const invites = (venueAttributes.invites as any[])?.filter(
          ({ data }) => data?.attributes?.id !== inviteId
        );

        (state.data.venue[venuePath].attributes as any).invites = invites;
      }
    });

    // matchers
    builder.addMatcher(isPendingAction, (state, { type, meta }) => {
      const action = getAction(IActionStates.pending, type, meta);
      setStateLoading(state, action);

      const { paginationKey } = meta.arg || {};
      if (paginationKey) {
        setStateLoading(state, paginationKey);
      }
    });
    builder.addMatcher(isRejectedAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.rejected, type, meta);
      setStateRejected(state, payload, action);

      const { paginationKey } = meta.arg || {};
      if (paginationKey) {
        setStateRejected(state, payload, paginationKey);
      }
    });
    builder.addMatcher(isCreatedFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);
      const { id, data, createdItem } = payload;

      setStateFulfilled(state, data, action, {
        id,
        createdItem: createdItem?.attributes,
      });
    });
    builder.addMatcher(isEditFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      let mergedState = payload;
      if (payload) {
        const key = Object.keys(omit(payload, "pagination"))[0] as IDataKeys;
        const id = Object.keys(payload[key])[0];
        const keyDataInStore = state.data[key];

        if (keyDataInStore) {
          delete keyDataInStore[id];
        }

        mergedState = {
          [key]: {
            ...state.data[key],
            ...payload[key],
          },
        };
      }

      setStateFulfilled(state, mergedState, action);
    });
    builder.addMatcher(isDeletedFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      // remove item from state
      state.data[payload.dataKey] = omit(state.data[payload.dataKey], payload.path);

      setStateFulfilled(state, undefined, action);
    });
    // update pagination in state
    // payload is merged in state by isFulfilledAction matcher
    builder.addMatcher(isPaginatedFulfilledAction, (state, { payload, meta }) => {
      const { page, paginationKey } = meta.arg;
      const { pagination } = payload || {};
      const key = Object.keys(omit(payload, "pagination"))[0] as IDataKeys;
      const payloadData = payload?.[key];

      // update pagination to state
      setStatePagination(state, paginationKey, page, payloadData, pagination);
      setStateFulfilled(state, {}, paginationKey);
    });
    builder.addMatcher(isReplaceFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      // remove the current items from the state
      const key = Object.keys(omit(payload, "pagination"))[0] as IDataKeys;
      state.data[key] = undefined;

      setStateFulfilled(state, payload, action);
    });
    builder.addMatcher(isVenueDetailFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);

      setStateFulfilled(state, getMergedVenueState(state, payload.venue, meta.arg.venueId), action);
    });
    // add linked info block contacts, places, files and images to redux
    builder.addMatcher(isInfoBlockDetailFulfilledAction, (state, { payload }) => {
      if (payload) {
        const infoBlocks = payload[keys(payload)[0]];
        const block: unknown = infoBlocks[keys(infoBlocks)[0]];
        const mergedState = infoBlockDetailMatcherHelper(state, block);

        setStateFulfilled(state, mergedState);
      }
    });
    builder.addMatcher(isFulfilledAction, (state, { type, payload, meta }) => {
      const action = getAction(IActionStates.fulfilled, type, meta);
      setStateFulfilled(state, payload, action, undefined, true);
    });
  },
});

export const { reducer: dataReducer } = dataSlice;
export const { clearActionMeta, clearActionPagination, clearVenueState } = dataSlice.actions;
